import React, { useState } from 'react';
import { Container, Row, Col, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  PaperAirplaneIcon,
  TicketIcon,
} from '@heroicons/react/24/solid';
import CompanyLayout from '../common/CompanyLayout';
import { supportAPI } from '../../services/api';

const NewTicket: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    subject: '',
    category: 'general',
    priority: 'medium',
    message: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!formData.subject.trim() || !formData.message.trim()) {
      setError('Please fill in all required fields.');
      return;
    }
    setLoading(true);
    try {
      const res = await supportAPI.createTicket(formData);
      navigate(`/company/support/${res.data.id}`);
    } catch {
      setError('Failed to create ticket. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <CompanyLayout>
      <Container fluid className="px-3 px-lg-4 page-content-padding" style={{ paddingTop: '24px' }}>
        <Button
          variant="link"
          className="d-flex align-items-center gap-1 p-0 mb-3"
          style={{ color: '#64748b', textDecoration: 'none', fontSize: '0.88rem', fontWeight: 500 }}
          onClick={() => navigate('/company/support')}
        >
          <ArrowLeftIcon style={{ width: '16px', height: '16px' }} />
          Back to tickets
        </Button>

        <Row className="justify-content-center">
          <Col lg={8}>
            <div style={{
              background: 'white',
              borderRadius: '16px',
              border: '1px solid #e2e8f0',
              padding: 'clamp(20px, 4vw, 36px)',
            }}>
              <div className="d-flex align-items-center gap-2 mb-1">
                <TicketIcon style={{ width: '22px', height: '22px', color: '#065f46' }} />
                <h5 className="mb-0" style={{ fontWeight: 700, color: '#0f172a' }}>New Support Ticket</h5>
              </div>
              <p style={{ color: '#94a3b8', fontSize: '0.85rem', marginBottom: '24px' }}>
                Describe your issue and we'll get back to you as soon as possible.
              </p>

              {error && (
                <Alert variant="danger" className="py-2" style={{ fontSize: '0.85rem', borderRadius: '10px' }}>
                  {error}
                </Alert>
              )}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label style={lblStyle}>Subject *</Form.Label>
                  <Form.Control
                    type="text"
                    name="subject"
                    placeholder="Brief description of your issue"
                    value={formData.subject}
                    onChange={handleChange}
                    required
                    maxLength={200}
                    style={inputStyle}
                  />
                </Form.Group>

                <Row className="g-3 mb-3">
                  <Col xs={12} sm={6}>
                    <Form.Group>
                      <Form.Label style={lblStyle}>Category</Form.Label>
                      <Form.Select
                        name="category"
                        value={formData.category}
                        onChange={handleChange}
                        style={inputStyle}
                      >
                        <option value="general">General Question</option>
                        <option value="billing">Billing & Payments</option>
                        <option value="technical">Technical Issue</option>
                        <option value="account">Account & Settings</option>
                        <option value="feature">Feature Request</option>
                        <option value="bug">Bug Report</option>
                        <option value="other">Other</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col xs={12} sm={6}>
                    <Form.Group>
                      <Form.Label style={lblStyle}>Priority</Form.Label>
                      <Form.Select
                        name="priority"
                        value={formData.priority}
                        onChange={handleChange}
                        style={inputStyle}
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                        <option value="urgent">Urgent</option>
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-4">
                  <Form.Label style={lblStyle}>Message *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={6}
                    name="message"
                    placeholder="Describe your issue in detail. Include any steps to reproduce, error messages, or screenshots links..."
                    value={formData.message}
                    onChange={handleChange}
                    required
                    style={{ ...inputStyle, resize: 'vertical' }}
                  />
                </Form.Group>

                <div className="d-flex justify-content-end gap-2">
                  <Button
                    variant="outline-secondary"
                    onClick={() => navigate('/company/support')}
                    style={{ borderRadius: '10px', fontWeight: 600, fontSize: '0.88rem', padding: '10px 20px' }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={loading}
                    className="d-flex align-items-center gap-2"
                    style={{
                      borderRadius: '10px',
                      padding: '10px 24px',
                      fontWeight: 600,
                      fontSize: '0.88rem',
                      background: 'linear-gradient(135deg, #065f46, #059669)',
                      color: 'white',
                      border: 'none',
                    }}
                  >
                    {loading ? (
                      <Spinner animation="border" size="sm" />
                    ) : (
                      <PaperAirplaneIcon style={{ width: '16px', height: '16px' }} />
                    )}
                    {loading ? 'Creating...' : 'Submit Ticket'}
                  </Button>
                </div>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
    </CompanyLayout>
  );
};

const lblStyle: React.CSSProperties = { fontWeight: 600, fontSize: '0.85rem', color: '#374151' };
const inputStyle: React.CSSProperties = {
  padding: '10px 14px',
  borderRadius: '10px',
  border: '1.5px solid #e2e8f0',
  fontSize: '0.9rem',
};

export default NewTicket;
